import { useState } from "react";
import { StatusBadge } from "./StatusBadge";
import { StarRating } from "./StarRating";
import { formatAddress, SERVICE_LABELS } from "../lib/format";
import type { Location } from "../lib/types";

interface Props {
  loc: Location | null;
  onClose: () => void;
}

export function Drawer({ loc, onClose }: Props) {
  const [imgIdx, setImgIdx] = useState(0);
  // Reset the image carousel when a different location opens — done during
  // render (React's "adjust state on prop change" pattern) rather than in an
  // effect, to avoid calling setState inside an effect (react-hooks/set-state-in-effect).
  const [prevLocId, setPrevLocId] = useState(loc?.location_id);
  if (loc?.location_id !== prevLocId) {
    setPrevLocId(loc?.location_id);
    setImgIdx(0);
  }
  if (!loc) return null;
  const services = Object.entries(loc.services ?? {})
    .filter(([k, v]) => v === true && SERVICE_LABELS[k])
    .map(([k]) => k);

  return (
    <div className="fixed inset-0 z-[9999] flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="w-full md:w-[520px] bg-white h-full overflow-y-auto shadow-2xl scroll-list">
        <div className="relative">
          {loc.images && loc.images[imgIdx] ? (
            <img
              src={loc.images[imgIdx].cdn_url}
              alt={loc.name}
              className="w-full h-56 object-cover bg-slate-100"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-full h-56 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-500">
              No image
            </div>
          )}
          <button
            onClick={onClose}
            aria-label="Close details"
            className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center shadow"
          >
            ✕
          </button>
        </div>

        <div className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">
            {loc.partner_chain?.display_name ?? "MoneyGram"}
          </div>
          <h2 className="text-xl font-bold text-slate-900 mt-0.5">
            {loc.name}
          </h2>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <StatusBadge loc={loc} />
            {loc.is_top_rated && (
              <span className="pill pill-amber">⭐ Top Rated</span>
            )}
            {loc.is_mg_exclusive && (
              <span className="pill pill-info">MG Exclusive</span>
            )}
            {loc.is_crypto_eligible && (
              <span className="pill pill-info">Crypto-Eligible</span>
            )}
          </div>

          <div className="text-sm text-slate-700 mt-3">
            {formatAddress(loc.address)}
          </div>
          {loc.phone && (
            <div className="text-sm text-slate-700 mt-1">📞 {loc.phone}</div>
          )}
          {loc.customer_guidance && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
              <span className="font-semibold">Wayfinding: </span>
              {loc.customer_guidance}
            </div>
          )}

          {(loc.rating ?? 0) > 0 && (
            <div className="mt-4">
              <StarRating
                value={loc.rating ?? 0}
                count={loc.rating_count ?? 0}
                size={16}
              />
            </div>
          )}

          <h3 className="mt-6 mb-2 text-sm font-semibold text-slate-900">
            Services
          </h3>
          <div className="grid grid-cols-2 gap-1.5">
            {services.map((k) => (
              <div
                key={k}
                className="flex items-center gap-2 text-sm text-slate-700"
              >
                <span className="text-emerald-600">✓</span>
                <span>{SERVICE_LABELS[k]}</span>
              </div>
            ))}
          </div>
          {(loc.services?.send_currencies?.length ||
            loc.services?.rec_currencies?.length) && (
            <div className="mt-3 text-xs text-slate-600 space-y-1">
              {loc.services?.send_currencies?.length ? (
                <div>
                  <span className="font-semibold">Send:</span>{" "}
                  {loc.services.send_currencies.join(", ")}
                </div>
              ) : null}
              {loc.services?.rec_currencies?.length ? (
                <div>
                  <span className="font-semibold">Receive:</span>{" "}
                  {loc.services.rec_currencies.join(", ")}
                </div>
              ) : null}
              {loc.services?.max_send_amount_usd ? (
                <div>
                  <span className="font-semibold">Max send:</span> $
                  {loc.services.max_send_amount_usd.toLocaleString()} USD
                </div>
              ) : null}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
